import { ChangeDetectorRef, Component, OnInit } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { MatButtonModule } from "@angular/material/button";

import { PinAutorizadorService } from "./pin-autorizador.service";
import {
  PIN_MAX,
  PIN_MIN,
  PinAutorizadorRes,
  problemaDelPin,
  soloDigitos,
} from "../../shared/model/security/pin-autorizador.model";

type Modo = "definir" | "cambiar";

/**
 * «PIN de autorización» en Ajustes, junto a la Contraseña. Aquí, y solo
 * aquí, cada autorizador define y cambia su PIN (decisión del 23/09: el PIN
 * cuelga del usuario, no de un módulo).
 *
 * Solo se ve si su perfil autoriza algo que pide PIN, o si ya tiene uno.
 * Definirlo pide la contraseña de COS; cambiarlo, el PIN actual. Lo que se
 * escribe se borra de los campos al terminar, salga bien o mal.
 */
@Component({
  selector: "app-pin-autorizador-panel",
  standalone: true,
  imports: [FormsModule, MatButtonModule],
  templateUrl: "./pin-autorizador-panel.component.html",
  styleUrls: ["./pin-autorizador-panel.component.css"],
})
export class PinAutorizadorPanelComponent implements OnInit {
  readonly PIN_MAX = PIN_MAX;

  estado: PinAutorizadorRes | null = null;
  modo: Modo | null = null;
  enviando = false;
  mensaje = "";
  tono: "error" | "ok" = "error";

  password = "";
  pinActual = "";
  pinNuevo = "";
  pinConfirma = "";

  constructor(
    private service: PinAutorizadorService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.consultar();
  }

  private consultar(): void {
    this.service.estado().subscribe({
      next: (res) => {
        // Sin estado no hay panel: no se ofrece lo que no se puede confirmar.
        this.estado = res.codigoError ? null : res;
        this.pinta();
      },
      error: () => {
        this.estado = null;
        this.pinta();
      },
    });
  }

  get visible(): boolean {
    return !!this.estado && (this.estado.puedeAutorizar === true || this.estado.tienePin === true);
  }

  get tienePin(): boolean {
    return this.estado?.tienePin === true;
  }

  get bloqueadoHasta(): string | null {
    return this.tienePin && this.estado?.bloqueadoHasta ? this.estado.bloqueadoHasta : null;
  }

  get etiqueta(): string {
    if (!this.tienePin) {
      return "Sin definir";
    }
    return this.bloqueadoHasta ? "Bloqueado" : "Definido";
  }

  get nota(): string {
    const e = this.estado;
    if (!e) {
      return "";
    }
    if (!this.tienePin) {
      return "Tu perfil autoriza con PIN: defínelo para poder firmar.";
    }
    if (this.bloqueadoHasta) {
      return `Bloqueado hasta las ${this.bloqueadoHasta} por cinco intentos fallidos. Para desbloquearlo antes, `
        + "pide a quien administra Empleados que lo restablezca.";
    }
    const n = e.intentosRestantes;
    if (n != null && n < 5) {
      return `Te ${n === 1 ? "queda 1 intento" : `quedan ${n} intentos`} antes de que se bloquee 15 minutos.`;
    }
    return "Se pide en cada firma. Nadie lo puede ver, ni quien administra.";
  }

  abrir(): void {
    if (this.bloqueadoHasta || this.enviando) {
      return;
    }
    this.limpiaCampos();
    this.di("");
    this.modo = this.tienePin ? "cambiar" : "definir";
    this.cdr.detectChanges();
    const primero = this.modo === "definir" ? "pin-password" : "pin-actual";
    setTimeout(() => document.getElementById(primero)?.focus());
  }

  cancelar(): void {
    if (this.enviando) {
      return;
    }
    this.limpiaCampos();
    this.di("");
    this.modo = null;
  }

  /** Solo dígitos, hasta 8; se corrige el campo mismo para que no quede una letra a la vista. */
  digitos(evento: Event): string {
    const input = evento.target as HTMLInputElement;
    const limpio = soloDigitos(input.value);
    if (input.value !== limpio) {
      input.value = limpio;
    }
    return limpio;
  }

  /** Lo que falta o no cumple, para decirlo antes de mandar. Nulo si está listo. */
  get problema(): string | null {
    if (this.modo === "definir" && !this.password) {
      return "Escribe tu contraseña de COS.";
    }
    if (this.modo === "cambiar" && !new RegExp(`^\\d{${PIN_MIN},${PIN_MAX}}$`).test(this.pinActual)) {
      return "Escribe tu PIN actual.";
    }
    if (!this.pinNuevo) {
      return "Escribe el PIN nuevo.";
    }
    const regla = problemaDelPin(this.pinNuevo);
    if (regla) {
      return regla;
    }
    if (this.modo === "cambiar" && this.pinNuevo === this.pinActual) {
      return "El PIN nuevo tiene que ser distinto del actual.";
    }
    if (this.pinConfirma !== this.pinNuevo) {
      return "La confirmación no coincide con el PIN nuevo.";
    }
    return null;
  }

  /**
   * La pista bajo los campos: la regla que no cumple el PIN nuevo en cuanto
   * tiene el largo mínimo, y que no coinciden cuando ya se escribió la
   * confirmación completa. Antes de eso no se regaña a nadie.
   */
  get pista(): string {
    if (this.pinNuevo.length >= PIN_MIN) {
      const regla = problemaDelPin(this.pinNuevo);
      if (regla) {
        return regla;
      }
      if (this.pinConfirma.length >= this.pinNuevo.length && this.pinConfirma !== this.pinNuevo) {
        return "La confirmación no coincide con el PIN nuevo.";
      }
    }
    return "";
  }

  guardar(): void {
    const modo = this.modo;
    if (!modo || this.enviando || this.problema) {
      return;
    }
    this.enviando = true;
    this.di("");
    const peticion = modo === "definir"
      ? this.service.establecer(this.password, this.pinNuevo)
      : this.service.cambiar(this.pinActual, this.pinNuevo);
    peticion.subscribe({
      next: (res) => {
        this.enviando = false;
        if (res.codigoError) {
          // Lo que se escribió no sirvió: se borra, no se deja para reintentar a ciegas.
          this.limpiaCampos();
          this.di(res.codigoError === "AUTH-001" ? "Tu sesión caducó. Vuelve a entrar."
            : res.mensajeError || "No se pudo guardar el PIN.");
          if (res.codigoError.startsWith("VAL-PIN") || res.codigoError === "VAL-003") {
            // Bloqueo, intentos o un PIN restablecido hace un momento: el estado cambió.
            this.consultar();
            if (res.codigoError === "VAL-PIN-BLOQ" || res.codigoError === "VAL-003") {
              this.modo = null;
            }
          }
          this.pinta();
          return;
        }
        this.limpiaCampos();
        this.modo = null;
        this.estado = res;
        this.di(modo === "definir" ? "PIN definido. Ya puedes firmar con él." : "PIN cambiado.", "ok");
        this.pinta();
      },
      error: () => {
        this.enviando = false;
        this.limpiaCampos();
        this.di("No se pudo guardar el PIN: sin conexión con el servidor.");
        this.pinta();
      },
    });
  }

  private limpiaCampos(): void {
    this.password = "";
    this.pinActual = "";
    this.pinNuevo = "";
    this.pinConfirma = "";
  }

  private di(texto: string, tono: "error" | "ok" = "error"): void {
    this.mensaje = texto;
    this.tono = tono;
  }

  /** La app corre sin zonas: una respuesta HTTP no repinta sola. */
  private pinta(): void {
    this.cdr.markForCheck();
  }
}
